import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { useCallback, useEffect, useState } from 'react'

import { getProvider, PUBLIC_RPCS } from '../data/stats/useMultiChainProvider'
import { LPHolder, useSunsetData } from '../data/sunset'
import { useActiveWeb3React } from './index'

// Minimal ERC20 ABI for balance check
const ERC20_ABI = ['function balanceOf(address) view returns (uint256)']

export interface LivePosition extends LPHolder {
  // Inherits all LPHolder fields, populated from on-chain data
}

/**
 * Hook that fetches LP balances on-chain for the connected wallet
 * across ALL networks (not just the connected one).
 * Uses public RPC endpoints to query each chain independently.
 */
export function useLivePositions(): {
  positions: LivePosition[]
  loading: boolean
  refresh: () => void
} {
  const { account } = useActiveWeb3React()
  const { pairs } = useSunsetData()
  const [positions, setPositions] = useState<LivePosition[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!account) {
      setPositions([])
      return
    }

    setLoading(true)
    try {
      // Group pairs by chainId
      const pairsByChain: { [chainId: number]: typeof pairs } = {}
      for (const pair of pairs) {
        if (!pairsByChain[pair.chainId]) {
          pairsByChain[pair.chainId] = []
        }
        pairsByChain[pair.chainId].push(pair)
      }

      // Query each chain in parallel
      const chainPromises = Object.entries(pairsByChain).map(async ([chainIdStr, chainPairs]) => {
        const chainId = parseInt(chainIdStr)

        // Skip chains we don't have RPC for
        if (!PUBLIC_RPCS[chainId]) {
          return []
        }

        const provider = getProvider(chainId)
        if (!provider) {
          return []
        }

        // Query all pairs on this chain
        const pairPromises = chainPairs.map(
          async (pair): Promise<LivePosition | null> => {
            try {
              const contract = new Contract(pair.pairAddress, ERC20_ABI, provider)
              const balance: BigNumber = await contract.balanceOf(account)

              if (balance.isZero()) return null

              // Calculate balance in human-readable format (LP tokens have 18 decimals)
              const balanceNum = Number(balance.toString()) / 1e18

              // Calculate USD value based on pair's TVL and total supply ratio
              // This is an approximation - ideally we'd fetch total supply too
              const balanceUsd =
                pair.tvlUsd > 0
                  ? (balanceNum / (pair.tvlUsd / pair.tvlUsd)) * pair.tvlUsd * (balanceNum / 1000)
                  : 0

              return {
                userAddress: account,
                pairAddress: pair.pairAddress,
                pairSymbol: pair.pairSymbol,
                chain: pair.chain,
                chainId: pair.chainId,
                balance: balanceNum,
                balanceUsd: balanceUsd,
                lastUpdated: new Date().toISOString()
              }
            } catch (err) {
              console.warn(
                `Failed to fetch balance for pair ${pair.pairAddress} on chain ${chainId}:`,
                err
              )
              return null
            }
          }
        )

        const results = await Promise.all(pairPromises)
        return results.filter((p): p is LivePosition => p !== null)
      })

      const chainResults = await Promise.all(chainPromises)
      const allPositions = chainResults.flat()

      setPositions(allPositions)
    } catch (error) {
      console.error('Error fetching live positions:', error)
      setPositions([])
    } finally {
      setLoading(false)
    }
  }, [account, pairs])

  // Fetch on mount and when dependencies change
  useEffect(() => {
    refresh()
  }, [refresh])

  return { positions, loading, refresh }
}
